import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { QuoteCard } from "@/components/QuoteCard";
import { useAuth } from "@/context/AuthContext";
import { greeting, moodColor, moodEmoji, moodLabel, fmtDate, fmtTime, toUtc } from "@/lib/mood";
import { SectionTitle, Empty } from "@/components/kit";
import { onPendingChange } from "@/lib/offline";
import { SupporterNotes } from "@/components/SupporterNotes";
import { BadgeCelebration } from "@/components/BadgeCelebration";
import { WindDownCard } from "@/components/bedtime/WindDownCard";
import { ForecastCard } from "@/components/forecast/ForecastCard";
import { UnopenedSupporters } from "@/components/UnopenedSupporters";
import { RenewalRequests } from "@/components/RenewalRequests";
import { ArticlePick } from "@/components/ArticlePick";
import { GardenPreview } from "@/components/garden/GardenPreview";
import { LetterArrivedCard } from "@/components/letters/LetterArrivedCard";
import { SosHomeCard } from "@/components/CalmButton";
import { GratitudeNudge } from "@/components/GratitudeNudge";
import { WelcomeTour } from "@/components/WelcomeTour";
import { PrivacyPromiseWelcome } from "@/components/PrivacyPromiseWelcome";
import { logError } from "@/lib/log";
import { SkillOfDay } from "@/components/SkillOfDay";

const QUICK = [
  ["🌬️", "Breathe", "/breathing"], ["🧘", "Meditate", "/meditation"], ["📝", "Journal", "/journal"], ["🎯", "Bingo", "/bingo"],
  ["💡", "Coping", "/coping"], ["🛡️", "Safety", "/safety"], ["📊", "Insights", "/insights"], ["🎤", "Voice", "/voice"],
];
const MICRO = [[2, "😢"], [4, "😔"], [6, "😐"], [8, "🙂"], [10, "😊"]];

const StatTile = ({ emoji, value, label, color, testId }) => (
  <div className="mm-card p-4 sm:p-5 text-center" data-testid={testId}>
    <div className="text-2xl">{emoji}</div>
    <div className="font-display text-2xl font-bold mt-1" style={{ color }}>{value}</div>
    <div className="text-xs text-mm-muted mt-0.5">{label}</div>
  </div>
);

const EntryCard = ({ e }) => {
  const m = e.mood_score || 5;
  return (
    <div className="mm-card p-5" data-testid={`recent-checkin-${e.id}`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{moodEmoji(m)}</span>
          <div>
            <div className="font-semibold" style={{ color: moodColor(m) }}>{moodLabel(m)}</div>
            <div className="text-xs text-mm-muted">{fmtDate(toUtc(e.timestamp), { weekday: "short", month: "short", day: "numeric" })} · {fmtTime(toUtc(e.timestamp))}</div>
          </div>
        </div>
        <div className="flex gap-2 text-sm">
          <span className="mm-soft px-3 py-1">Mood {m}/10</span>
          {e.stress_level != null || e.anxiety_level != null ? <span className="mm-soft px-3 py-1">Stress {e.stress_level ?? e.anxiety_level}/10</span> : null}
        </div>
      </div>
      {e.journal_entry && <p className="text-sm text-mm-muted mt-3 line-clamp-2">“{e.journal_entry}”</p>}
    </div>
  );
};

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [promiseDone, setPromiseDone] = useState(false);
  const [recent, setRecent] = useState([]);
  const g = greeting();

  const load = useCallback(() => {
    api.get("/stats/overview").then((r) => setStats(r.data)).catch((e) => logError("Stats load failed:", e));
    api.get("/checkins?limit=5").then((r) => setRecent(r.data)).catch((e) => logError("Check-ins load failed:", e));
  }, []);
  useEffect(() => {
    load();
    return onPendingChange(load);
  }, [load]);

  const micro = async (score, emoji) => {
    await api.post("/micro-moods", { mood_score: score, emoji });
    toast.success(`Mood snapshot saved ${emoji}`);
    load();
  };

  const s = stats || { avg_mood: 0, avg_energy: 0, checkins_this_week: 0, current_streak: 0 };
  return (
    <div data-testid="home-page">
      {user?.id && (user.privacy_promise_pending && !promiseDone
        ? <PrivacyPromiseWelcome onDone={() => setPromiseDone(true)} />
        : <WelcomeTour userId={user.id} />)}
      <div className="flex justify-between items-start mb-8 mm-fade-up">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="home-greeting">{g.text}, {user?.name?.split(" ")[0]} {g.emoji}</h1>
          <p className="text-mm-muted mt-1">How are you feeling today?</p>
        </div>
        {s.current_streak > 0 && (
          <div className="mm-soft px-4 py-2 flex items-center gap-1 font-bold text-mm-primary" data-testid="home-streak"><Flame size={18} /> {s.current_streak}</div>
        )}
      </div>

      <LetterArrivedCard />
      <GratitudeNudge />
      <WindDownCard />
      <ForecastCard />
      <UnopenedSupporters compact />
      <SosHomeCard />
      {user?.id && <BadgeCelebration userId={user.id} />}
      <SkillOfDay />
      <ArticlePick />
      <RenewalRequests />
      <SupporterNotes />

      <QuoteCard />
      <GardenPreview />

      <div className="grid grid-cols-3 gap-3 sm:gap-4 mm-stagger">
        <StatTile testId="home-stat-mood" emoji={s.avg_mood ? moodEmoji(Math.round(s.avg_mood)) : "🌱"} value={s.avg_mood ? Number(s.avg_mood).toFixed(1) : "—"} label="Avg Mood" color={moodColor(Math.round(s.avg_mood))} />
        <StatTile testId="home-stat-energy" emoji={s.avg_energy >= 6 ? "⚡" : "🔋"} value={s.avg_energy ? Number(s.avg_energy).toFixed(1) : "—"} label="Avg Energy" color="var(--mm-secondary)" />
        <StatTile testId="home-stat-week" emoji="📝" value={s.checkins_this_week} label="This Week" color="var(--mm-tertiary)" />
      </div>

      <div className="mm-card p-5 mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="font-semibold">Quick mood snapshot</span>
          <div className="flex gap-2">
            {MICRO.map(([v, e]) => (
              <button key={v} onClick={() => micro(v, e)} data-testid={`home-micro-mood-${v}`} className="text-3xl hover:scale-125 transition-transform">{e}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mm-card p-5 mt-6">
        <button className="mm-btn w-full !rounded-2xl !py-4 text-lg" onClick={() => nav("/checkin")} data-testid="home-checkin-button">
          <PlusCircle size={22} /> Check In Now
        </button>
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mt-4">
          {QUICK.map(([e, l, to]) => (
            <Link key={l} to={to} className="mm-soft py-4 flex flex-col items-center hover:-translate-y-0.5 transition-transform" data-testid={`home-quick-${l.toLowerCase()}`}>
              <span className="text-2xl">{e}</span><span className="text-xs font-medium mt-1">{l}</span>
            </Link>
          ))}
        </div>
      </div>

      <SectionTitle right={<Link to="/calendar" className="text-sm text-mm-primary font-semibold" data-testid="home-view-all">View All</Link>}>Recent Check-ins 📋</SectionTitle>
      <div className="space-y-3">
        {recent.length ? recent.slice(0, 3).map((e) => <EntryCard key={e.id} e={e} />) : <Empty emoji="🌱" title="No check-ins yet" text="Your first check-in takes about two minutes." cta="Start check-in" to="/checkin" />}
      </div>
    </div>
  );
}
